import React, { useEffect, useState } from 'react';
import api from './api';

// Every Update PO pressed in PO Receiving: what was received, who by, and whether
// Striven took it. For the Stats page (an org's own receipts). Renders nothing for an
// org that doesn't have PO Receiving, since the backend answers those with a 402.

export const formatWhen = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// The few Striven refusals seen so far, in plain words; anything else shows its start.
export const explainReason = (reason) => {
  if (!reason) return '';
  if (/vendor currency/i.test(reason)) return "The vendor isn't in CAD, and Striven won't create a bill for one.";
  if (/does not allow billing/i.test(reason)) return "The PO isn't approved in Striven yet, so it can't be billed.";
  if (/already been fully received/i.test(reason)) return 'This PO was already fully received.';
  const text = reason.replace(/\s+/g, ' ').trim();
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
};

// What a person would key into Striven by hand: who, when and what, one item per line.
export const summaryText = (row) => {
  const when = (() => { try { return new Date(row.at).toLocaleString(); } catch { return ''; } })();
  const lines = (row.items || []).map(i => `• ${i.qty} x ${i.name}`);
  return [`Received by ${row.by || 'unknown'} on ${when}, PO ${row.po}:`, ...lines].join('\n');
};

export async function getAuthHeaders(msalInstance) {
  const authProvider = localStorage.getItem('authProvider') || 'microsoft';
  if (authProvider === 'google') {
    return { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
  }
  const configRes = await api.get('/api/config/azure');
  const { clientId } = configRes.data;
  const accounts = msalInstance.getAllAccounts();
  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: [`api://${clientId}/access_as_user`],
    account: accounts[0],
  });
  return { Authorization: `Bearer ${tokenResponse.accessToken}` };
}

// The list of receipts, each expandable to its items. Shared by the Stats page (an org's
// own receipts) and the Logs page (every org, with showOrg).
export function ReceivingRows({ receipts, showOrg = false }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div style={s.list}>
      {receipts.map(r => {
        const ok = r.status === 'received';
        const isOpen = open.has(r.id);
        return (
          <div key={r.id} style={s.row}>
            <button type="button" style={s.rowBtn} onClick={() => toggle(r.id)} aria-expanded={isOpen}>
              <div style={s.rowHead}>
                <span style={{ ...s.chevron, transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                <span style={s.po}>PO {r.po}</span>
                <span style={{ ...s.badge, ...(ok ? s.badgeOk : r.done ? s.badgeDone : s.badgeBad) }}>
                  {ok ? 'Received' : r.done ? 'Done by hand' : 'Not in Striven'}
                </span>
                {showOrg && r.org && <span style={s.orgTag}>{r.org}</span>}
                <span style={s.meta}>{r.totalUnits} units · {(r.items || []).length} item{(r.items || []).length === 1 ? '' : 's'}</span>
                <span style={s.time}>{r.by} · {formatWhen(r.at)}</span>
              </div>
            </button>
            {isOpen && (
              <div style={s.details}>
                {!ok && <div style={s.reason}>{explainReason(r.reason)}</div>}
                {(r.problems || []).map((p, i) => <div key={i} style={s.problem}>{explainReason(p)}</div>)}
                <table style={s.table}>
                  <tbody>
                    {(r.items || []).map((i, idx) => (
                      <tr key={idx}><td style={s.qty}>{i.qty}</td><td>{i.name}</td></tr>
                    ))}
                  </tbody>
                </table>
                {r.billId && <div style={s.meta}>Bill {r.billId}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ReceivingHistoryLog({ msalInstance, wrap = (content) => content, onLoaded }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading'); // loading | ok | hidden | error
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders(msalInstance);
        const res = await api.get('/api/receiving/history/all', { headers });
        setRows(res.data || []);
        setState('ok');
        const list = res.data || [];
        onLoaded?.({ available: true, count: list.length, notInStriven: list.filter(r => r.status !== 'received' && !r.done).length });
      } catch (err) {
        // 402: this org doesn't have PO Receiving. Nothing to show, so say nothing.
        const unavailable = err.response?.status === 402;
        setState(unavailable ? 'hidden' : 'error');
        onLoaded?.({ available: !unavailable });
      }
    })();
  }, []);

  if (state === 'hidden') return null;
  if (state === 'loading') return wrap(<p style={s.empty}>Loading…</p>);
  if (state === 'error') return wrap(<p style={s.errorText}>Could not load the PO receiving history.</p>);

  const needle = query.trim().toLowerCase();
  const visible = rows.filter(r => !needle
    || String(r.po).toLowerCase().includes(needle)
    || (r.by || '').toLowerCase().includes(needle)
    || (r.items || []).some(i => i.name.toLowerCase().includes(needle)));

  return wrap(
    <>
      <p style={s.sub}>
        Every time Update PO was pressed. A receipt marked "Not in Striven" was scanned but Striven refused it,
        so it needs receiving by hand (see Unfinished Orders).
      </p>
      <div style={s.controls}>
        <input
          style={s.search}
          type="search"
          placeholder="Search PO, person or item"
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label="Search PO receiving history"
        />
      </div>
      {visible.length === 0 && <p style={s.empty}>{rows.length === 0 ? 'No receipts yet.' : 'Nothing matches.'}</p>}
      <ReceivingRows receipts={visible} />
    </>
  );
}

const s = {
  controls: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  search: { flex: '1 1 220px', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  sub: { fontSize: 13, color: '#888', marginBottom: 14, marginTop: 0, lineHeight: 1.5 },
  errorText: { color: '#b91c1c', fontSize: 14 },
  empty: { color: '#aaa', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  rowBtn: { display: 'block', width: '100%', padding: 0, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' },
  rowHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  chevron: { fontSize: 10, color: '#999', transition: 'transform 0.15s', display: 'inline-block' },
  po: { fontSize: 14, fontWeight: 700, color: '#333' },
  badge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: 0.3 },
  badgeOk: { background: '#dcfce7', color: '#166534' },
  badgeBad: { background: '#fff7ed', color: '#c2410c' },
  badgeDone: { background: '#f3f4f6', color: '#555' },
  meta: { fontSize: 12, color: '#888' },
  orgTag: { fontSize: 12, color: '#666', background: '#f3f4f6', padding: '2px 8px', borderRadius: 100 },
  time: { fontSize: 12, color: '#999', marginLeft: 'auto' },
  details: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#444' },
  reason: { color: '#c2410c', fontWeight: 600 },
  problem: { color: '#92400e', fontSize: 12 },
  table: { borderCollapse: 'collapse', fontSize: 13 },
  qty: { paddingRight: 14, fontWeight: 600, textAlign: 'right', verticalAlign: 'top' },
};
